import { supabase } from '@/lib/supabase';
import { stockService } from './stock';
import { errorLogger } from '@/lib/errorLogger';
import { cartCrudService } from './cart-crud.service';
import type { AddToCartResponse, CartServiceResponse, FindOfferResponse, OrderType } from './cart.types';

export class CartItemsService {

  async addToCart(
    productId: string, 
    offerId: string, 
    quantity: number, 
    userId?: string, 
    sessionId?: string
  ): Promise<AddToCartResponse> {
    try {
      // Get product and offer info
      const { data: productData } = await supabase
        .from('products')
        .select('restaurant_id, name, is_available')
        .eq('id', productId)
        .single();

      const { data: offerData } = await supabase
        .from('product_offers')
        .select('id, offer_type, discounted_price, is_active')
        .eq('id', offerId)
        .single();

      if (!productData || !offerData) {
        return { success: false, error: '상품 정보를 찾을 수 없습니다.' };
      }

      if (!offerData.is_active) {
        return { success: false, error: '현재 제공되지 않는 할인 유형입니다.' };
      }

      if (!productData.is_available) {
        return { success: false, error: '현재 판매 중이지 않은 상품입니다.' };
      }

      // Check stock availability using stockService
      const today = new Date().toISOString().split('T')[0];
      const stockData = await stockService.getProductStock(productId, today, true);

      if (!stockData) {
        return { success: false, error: '재고 정보를 확인할 수 없습니다.' };
      }

      const currentStock = stockData.current_stock;
      if (currentStock < quantity) {
        return { success: false, error: '재고가 부족합니다.' };
      }

      // Get or create cart
      let cart = await cartCrudService.getCurrentCart(userId, sessionId);
      
      if (!cart) {
        cart = await cartCrudService.createCart(productData.restaurant_id, userId, sessionId);
        if (!cart) {
          return { success: false, error: '장바구니 생성에 실패했습니다.' };
        }
      } else if (cart.restaurant_id !== productData.restaurant_id) {
        return { 
          success: false, 
          error: '다른 매장의 상품입니다. 기존 장바구니를 초기화하고 다시 시도해주세요.',
          needsRestaurantChange: true,
          cartId: cart.id
        };
      }

      // Check if item already exists in cart
      const { data: existingItem, error: checkError } = await supabase
        .from('cart_items')
        .select('*')
        .eq('cart_id', cart.id)
        .eq('product_id', productId)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid PGRST116 errors

      // Handle RLS policy errors on cart_items access
      if (checkError) {
        errorLogger.logCartError(
          checkError, 
          'check_existing_item', 
          cart.id, 
          productId, 
          userId
        );
        
        // Check for authentication or RLS policy issues
        if (errorLogger.isRLSError(checkError) || checkError.code === '42P01') {
          return { 
            success: false, 
            error: errorLogger.getUserFriendlyMessage(checkError, '장바구니 접근 권한이 없습니다. 로그인 상태를 확인해주세요.')
          };
        }
        
        // Handle other unexpected errors
        return {
          success: false,
          error: errorLogger.getUserFriendlyMessage(checkError, '장바구니 확인 중 오류가 발생했습니다.')
        };
      }

      if (existingItem) {
        // Update quantity
        const newQuantity = existingItem.quantity + quantity;

        // Recheck stock for new total quantity
        const recheckData = await stockService.getProductStock(productId, today, true);

        if (!recheckData) {
          return { success: false, error: '재고 정보를 확인할 수 없습니다.' };
        }

        const availableStock = recheckData.current_stock;
        if (availableStock < newQuantity) {
          return { success: false, error: `총 수량 ${newQuantity}개는 재고를 초과합니다. (재고: ${availableStock}개)` };
        }

        const { error } = await supabase
          .from('cart_items')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingItem.id);

        if (error) {
          console.error('Cart item update error:', error);
          
          // Check for RLS policy errors
          if (error.code === '42501' || error.message.includes('policy')) {
            return { 
              success: false, 
              error: '장바구니 업데이트 권한이 없습니다. 로그인 상태를 확인해주세요.' 
            };
          }
          
          return { 
            success: false, 
            error: `장바구니 업데이트에 실패했습니다. (${error.code}: ${error.message})` 
          };
        }

        // Stock will be consumed at checkout
      } else {
        // Check cart item limit (max 5 different products)
        if (cart.item_count >= 5) {
          return { success: false, error: '장바구니에는 최대 5종류의 상품만 담을 수 있습니다.' };
        }

        // Add new item
        const { error } = await supabase
          .from('cart_items')
          .insert({
            cart_id: cart.id,
            product_id: productId,
            offer_id: offerId,
            quantity,
            unit_price: offerData.discounted_price
          });

        if (error) {
          console.error('Cart item insertion error:', error);
          
          // Check for RLS policy errors
          if (error.code === '42501' || error.message.includes('policy')) {
            return { 
              success: false, 
              error: '장바구니 접근 권한이 없습니다. 로그인 상태를 확인해주세요.' 
            };
          }
          
          // Check for foreign key violations
          if (error.code === '23503') {
            return { 
              success: false, 
              error: '상품 또는 할인 정보가 존재하지 않습니다.' 
            };
          }
          
          return { 
            success: false, 
            error: `장바구니에 추가하는데 실패했습니다. (${error.code}: ${error.message})` 
          };
        }

        // Update item_count
        await cartCrudService.updateCartItemCount(cart.id);
        
        // Stock will be consumed at checkout
      }

      return { success: true, cartId: cart.id };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error: '알 수 없는 오류가 발생했습니다.' };
    }
  }

  async removeFromCart(cartItemId: string): Promise<CartServiceResponse> {
    try {
      // Get cart item info
      const { data: cartItem } = await supabase
        .from('cart_items')
        .select('cart_id, product_id, quantity')
        .eq('id', cartItemId)
        .single();

      if (!cartItem) {
        return { success: false, error: '장바구니 아이템을 찾을 수 없습니다.' };
      }

      // Remove cart item
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', cartItemId);

      if (error) {
        return { success: false, error: '아이템 삭제에 실패했습니다.' };
      }

      // Update cart item count
      await cartCrudService.updateCartItemCount(cartItem.cart_id);

      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, error: '알 수 없는 오류가 발생했습니다.' };
    }
  }

  async updateCartItemQuantity(
    cartItemId: string, 
    newQuantity: number
  ): Promise<CartServiceResponse> {
    try {
      if (newQuantity <= 0) {
        return this.removeFromCart(cartItemId);
      }

      // Get current cart item
      const { data: cartItem } = await supabase
        .from('cart_items')
        .select('*')
        .eq('id', cartItemId)
        .single();

      if (!cartItem) {
        return { success: false, error: '장바구니 아이템을 찾을 수 없습니다.' };
      }

      // Check stock availability
      const today = new Date().toISOString().split('T')[0];
      const stockData = await stockService.getProductStock(cartItem.product_id, today, true);

      if (!stockData) {
        return { success: false, error: '재고 정보를 확인할 수 없습니다.' };
      }

      const currentStock = stockData.current_stock;
      if (currentStock < newQuantity) {
        return { success: false, error: `재고가 부족합니다. (재고: ${currentStock}개, 요청: ${newQuantity}개)` };
      }

      // Update cart item
      const { error } = await supabase
        .from('cart_items')
        .update({ 
          quantity: newQuantity,
          updated_at: new Date().toISOString()
        })
        .eq('id', cartItemId);

      if (error) {
        return { success: false, error: '수량 업데이트에 실패했습니다.' };
      }

      // Stock will be consumed at checkout

      return { success: true };
    } catch (error) {
      console.error('Error updating cart item quantity:', error);
      return { success: false, error: '알 수 없는 오류가 발생했습니다.' };
    }
  }

  /**
   * 상품의 올바른 offer_id를 찾는 헬퍼 메서드
   */
  async findOfferByType(
    productId: string, 
    orderType: OrderType
  ): Promise<FindOfferResponse> {
    try {
      const { data: offers } = await supabase
        .from('product_offers')
        .select('id, offer_type, is_active')
        .eq('product_id', productId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (!offers || offers.length === 0) {
        return { success: false, error: '해당 상품에 대한 할인 정보를 찾을 수 없습니다.' };
      }

      // 1. 정확한 주문 유형과 일치하는 offer 찾기
      const exactMatch = offers.find(offer => offer.offer_type === orderType);
      if (exactMatch) {
        return { success: true, offerId: exactMatch.id };
      }

      // 2. 대체 전략: 활성화된 첫 번째 offer 사용
      const fallbackOffer = offers[0];
      console.warn(`No exact offer match for ${orderType}, using fallback: ${fallbackOffer.offer_type}`);
      
      return { success: true, offerId: fallbackOffer.id };
    } catch (error) {
      console.error('Error finding offer by type:', error);
      return { success: false, error: '할인 정보를 조회하는데 실패했습니다.' };
    }
  }
}

export const cartItemsService = new CartItemsService();